import { ACCOUNT_FLAG_CODES, type AccountFlag } from '@/types/models';

const LOWER_TO_FLAG: Record<string, AccountFlag> = Object.fromEntries(
	ACCOUNT_FLAG_CODES.map((c) => [c.toLowerCase(), c]),
) as Record<string, AccountFlag>;

/**
 * QuickBooks customer-name synonyms / common misspellings → canonical AccountFlag.
 *
 * Property managers hand-type flag markers into QB customer names, so the same
 * concept shows up under several spellings. This is the authoritative mapping
 * for imports — extend it (don't fork the parser) when a new import surfaces a
 * new spelling. Keys are compared lowercased.
 *
 * Sources observed so far:
 *  - Avery (Jun 2026): `(l/dl)`, `(NOPay/f-Aassoc/l)`, `(NOPay/f-Asoc/l)`, `(l)`
 */
const FLAG_ALIASES: Record<string, AccountFlag> = {
	// "NoPay" = stop auto-billing this account → Pause Payments (suppresses dues + late fees).
	'nopay': 'X',
	// Foreclosure-by-Association misspellings (canonical is "F-Assoc").
	'f-assoc': 'F-Assoc',
	'f-asoc': 'F-Assoc',
	'f-aassoc': 'F-Assoc',
	'fassoc': 'F-Assoc',
	// Foreclosure-by-Bank variant.
	'f-bank': 'F-Bank',
	'fbank': 'F-Bank',
	// Chapter-13 spelling variants.
	'pre-ch-13': 'Pre-Ch13',
	'prech13': 'Pre-Ch13',
	'post-ch-13': 'Post-Ch13',
	'postch13': 'Post-Ch13',
};

const resolveFlag = (token: string): AccountFlag | undefined => {
	const key = token.trim().toLowerCase();
	return LOWER_TO_FLAG[key] ?? FLAG_ALIASES[key];
};

// Any parenthetical group. The inner text may be a single code or a list
// delimited by "/" or "," — e.g. "(l/dl)" or "(NOPay/f-Assoc/l)". A group is
// only consumed when EVERY part resolves to a known flag; otherwise it's left
// in place. That guard is what keeps building/unit groupings like "(P3/5)",
// "(45)", "(1/2)" and annotations like "(2024)"/"(CLOSED, 4825PC)" intact —
// at least one of their parts never resolves to a flag.
const TOKEN_RE = /\(([^)]+)\)/g;

export interface ParsedNameFlags {
	name: string;
	flags: AccountFlag[];
}

export const parseNameFlags = (raw: string): ParsedNameFlags => {
	const seen = new Set<AccountFlag>();
	let stripped = raw.replace(TOKEN_RE, (full, inner: string) => {
		const parts = inner.split(/[/,]/).map((p) => p.trim()).filter(Boolean);
		if (parts.length === 0) return full;
		const resolved = parts.map(resolveFlag);
		// All-or-nothing: a single unrecognized part means this isn't a flag list
		// (it's a building/unit grouping or an unrelated annotation) — leave it.
		if (resolved.some((r) => r === undefined)) return full;
		for (const r of resolved) seen.add(r as AccountFlag);
		return '';
	});
	// QuickBooks marks ACH/autopay accounts with a backtick "`" in the customer
	// name or address (e.g. "88WC - ` Horner"). Treat any backtick as the ACH
	// flag and strip it from the cleaned name.
	if (stripped.includes('`')) {
		seen.add('ACH');
		stripped = stripped.replace(/`/g, '');
	}
	const collapsed = stripped
		.replace(/\s{2,}/g, ' ')
		.replace(/\s+([-·])\s+/g, ' $1 ')
		.trim();
	return { name: collapsed, flags: [...seen] };
};
